import {Blankslate} from '../Blankslate'
import {BypassList} from '@github-ui/bypass-actors/BypassList'
import {BypassSelectPanel} from '@github-ui/bypass-actors/BypassSelectPanel'
import {ActorBypassMode, type BypassActor, type SupportedBypassMode} from '@github-ui/bypass-actors/types'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {RulesetRoutePayload} from '@github-ui/repos-rules-types/rules-types'
import type {FC} from 'react'
import {useMemo, useRef} from 'react'

import {capitalize} from '../../helpers/string'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {useRuleStrings} from '../../hooks/use-rule-strings'
import type {BypassListPanelProps} from './BypassListPanel'
import {PanelHeader} from './conditions/PanelHeader'

export const LegacyBypassListPanel: FC<BypassListPanelProps> = ({
  readOnly,
  bypassActors,
  addBypassActor,
  removeBypassActor,
  rulesetTarget,
  updateBypassActor,
}: BypassListPanelProps) => {
  const {baseAvatarUrl, sourceType} = useRoutePayload<RulesetRoutePayload>()
  const {resolvePath} = useRelativeNavigation()
  const {rulesetOrPolicy} = useRuleStrings()

  const supportedBypassModes: SupportedBypassMode[] = [
    {
      mode: ActorBypassMode.ALWAYS,
      description: `The ${rulesetOrPolicy} will be evaluated and the selected actor(s) will be prompted to bypass`,
    },
    {
      mode: ActorBypassMode.EXEMPT,
      description: `The ${rulesetOrPolicy} will not be evaluated and no bypass prompt will be shown`,
    },
  ]
  if (rulesetTarget === 'branch') {
    supportedBypassModes.push({
      mode: ActorBypassMode.PRS_ONLY,
      description: `The ${rulesetOrPolicy} will be enforced on command line changes and the selected actor(s) will be prompted to bypass only in a pull request`,
    })
  }

  const bypassSuggestionsUrl = resolvePath('bypass_suggestions')

  const enabledBypassActors = useMemo(() => {
    const isAdminRole = (actor: BypassActor) => actor.name === 'admin' && actor.actorType === 'RepositoryRole'
    const enabled = bypassActors.filter(({_enabled}) => _enabled)
    return [...enabled.filter(isAdminRole), ...enabled.filter(actor => !isAdminRole(actor))]
  }, [bypassActors])

  const bypassSelectPanelAnchorRef = useRef<HTMLButtonElement>(null)

  let listName = 'bypass list'
  let bypassTitle = 'Add bypass'
  let reviewerGroup = 'roles, teams, agents, apps, and users'

  if (rulesetTarget === 'repository') {
    listName = 'allow list'
    bypassTitle = 'Add actors'
  }
  if (sourceType === 'enterprise') {
    reviewerGroup = 'roles'
  }

  let subtext = `Exempt ${reviewerGroup} from this ${rulesetOrPolicy} by adding them to the ${listName}.`

  if (rulesetTarget === 'push') {
    if (sourceType === 'enterprise') {
      subtext = 'Select the roles that can bypass and also approve bypass requests.'
    } else {
      subtext = `Select the roles and teams that can bypass and also approve bypass requests. Add the bots and agents that can bypass this ${rulesetOrPolicy}.`
    }
  }

  return (
    <div>
      <div className="tmp-mb-3">
        <PanelHeader
          title={capitalize(listName)}
          subtitle={subtext}
          trailing={
            !readOnly && (
              <div className="ml-auto">
                <BypassSelectPanel
                  title={bypassTitle}
                  baseAvatarUrl={baseAvatarUrl}
                  enabledBypassActors={enabledBypassActors}
                  addBypassActor={addBypassActor}
                  removeBypassActor={removeBypassActor}
                  suggestionsUrl={bypassSuggestionsUrl}
                  addReviewerSubtitle={`Choose which ${reviewerGroup} can bypass this ${rulesetOrPolicy}`}
                  bypassSelectPanelAnchorRef={bypassSelectPanelAnchorRef}
                />
              </div>
            )
          }
        />
      </div>
      <div className="Box">
        {enabledBypassActors.length > 0 ? (
          <ul>
            <BypassList
              readOnly={readOnly}
              removeBypassActor={removeBypassActor}
              refocusOnRemoval={() => {
                setTimeout(() => {
                  bypassSelectPanelAnchorRef.current?.focus()
                })
              }}
              supportedBypassModes={supportedBypassModes}
              updateBypassActor={updateBypassActor}
              enabledBypassActors={enabledBypassActors}
              baseAvatarUrl={baseAvatarUrl}
            />
          </ul>
        ) : (
          <Blankslate heading={`${capitalize(listName)} is empty`} />
        )}
      </div>
    </div>
  )
}
